import { ApolloQueryResult, NormalizedCacheObject } from 'apollo-boost';
import fetchJsonp from 'fetch-jsonp';

import { getMutationIdMutation } from '../graphql/mutations/grid/grid';
import {
    authorizationQuery,
    clientMetrikaCountersQuery,
    commonQuery,
    headerClientInfo,
    headerInfo,
    headerMenu,
    walletInfo
} from '../graphql/queries/grid/common';
import { clientFreelancerQuery } from '../graphql/queries/grid/freelancers';
import { clientMobileAppsQuery } from '../graphql/queries/grid/mobile-apps';
import {
    mapApiClientToClientData,
    mapApiWalletsToWalletData,
    mapCampaignRowTypeToCampaignType,
    mapCommonResponseToAccessRights,
    mapOfficeContacts,
    mapRegionContacts
} from '../mappers/common/common';
import { constantMapper } from '../mappers/constants';
import { getClientConstData } from '../reducers/common';
import { GlobalSettings } from '../service/GlobalSettings/GlobalSettings';
import { UrlHelpers } from '../service/UrlHelpers';
import { CommonData } from '../typings/common';
import { ConstCurrencyData } from '../typings/constants';
import { dealStatusForFilter } from '../typings/deals';
import {
    AuthorizationQuery,
    ClientFreelancerQuery,
    ClientMetrikaCountersQuery,
    ClientMobileAppsQuery,
    CommonQuery,
    getMutationId,
    HeaderClientInfoQuery,
    HeaderInfoQuery,
    MenuHeaderQuery,
    WalletInfoQuery
} from '../typings/graphql-grid';
import { ClientMobileAppsData } from '../typings/state';

import { DNAApolloClient } from './DNAApolloClient/DNAApolloClient';

export interface FetchUserAccountInfoRequest {
    uid: string;
    login: string;
    displayName: {
        name: string;
        default_avatar: string;
    };
    defaultEmail: string;
}

export interface FetchUserAccountsInfoRequest {
    accounts?: Array<FetchUserAccountInfoRequest>;
    'can-add-more': boolean;
    default_uid: string;
}

export class CommonDataProvider {
    constructor(private client: DNAApolloClient<NormalizedCacheObject>) {}

    public fetchAuthorizationData() {
        return this.client.query<AuthorizationQuery>({
            query: authorizationQuery
        });
    }

    public fetchClientFreelancer(login: string) {
        return this.client.query<ClientFreelancerQuery>({
            query: clientFreelancerQuery,
            variables: {
                login
            }
        });
    }

    public fetchClientMobileApps(login: string): Promise<ClientMobileAppsData> {
        return this.client
            .query<ClientMobileAppsQuery>({
                query: clientMobileAppsQuery,
                variables: {
                    login
                }
            })
            .then(response => {
                const client = response.data.client;

                return {
                    canViewMobileApps: client.access.canViewMobileApps,
                    canEditMobileApps: client.access.canEditMobileApps,
                    canEditMobileAppDomain:
                        client.access.canEditMobileAppDomain,
                    canViewMobileAppDomain: client.access.canViewMobileAppDomain
                };
            });
    }

    public async fetchUserAccountsInfo(): Promise<fetchJsonp.Response> {
        return fetchJsonp(
            `${GlobalSettings.accountsUrl}?yu=${GlobalSettings.yu}`
        );
    }

    public async fetchHeaderInfo(login: string) {
        return this.client.query<HeaderInfoQuery>({
            query: headerInfo,
            variables: {
                login,
                dealsFilter: {
                    statusContains: [dealStatusForFilter.RECEIVED]
                }
            }
        });
    }

    public async fetchHeaderWalletInfo(login: string) {
        return this.client.query<WalletInfoQuery>({
            query: walletInfo,
            variables: {
                login
            }
        });
    }

    public async fetchHeaderClientInfo(login: string) {
        return this.client.query<HeaderClientInfoQuery>({
            query: headerClientInfo,
            variables: {
                login
            }
        });
    }

    public async fetchHeaderMenu() {
        return this.client.query<MenuHeaderQuery>({
            query: headerMenu
        });
    }

    public async fetchClientMetrikaCounters(
        login: string
    ): Promise<ApolloQueryResult<ClientMetrikaCountersQuery>> {
        return this.client.query<ClientMetrikaCountersQuery>({
            query: clientMetrikaCountersQuery,
            variables: {
                login
            }
        });
    }

    /**
     * Дожидается синхронизации всех операций клиента в YT
     * @see DIRECT-86259
     */
    public awaitClientMutations(login: string): Promise<{}> {
        return this.client.awaitMutate<getMutationId>({
            mutation: getMutationIdMutation,
            login,
            variables: { login }
        });
    }

    public getData(login: string): Promise<CommonData> {
        return this.client
            .query<CommonQuery>({
                query: commonQuery,
                variables: {
                    login
                }
            })
            .then(result => {
                const constants = result.data.constants;
                const agencyOfflineReportMaximumDate =
                    constants.agencyOfflineReportMaximumDate;
                const accessRights = mapCommonResponseToAccessRights(
                    result.data
                );
                const availableCampaignTypes = accessRights.canCreateCampaignTypes.map(
                    campType => {
                        return mapCampaignRowTypeToCampaignType(campType);
                    }
                );
                const currencyConstants = constants.currencyConstants.reduce(
                    (acc, item) => {
                        acc[item.code] = constantMapper(item);

                        return acc;
                    },
                    {}
                ) as ConstCurrencyData;
                const strategyConstants = constants.strategyConstants;
                const canvasDomain = UrlHelpers.getCanvasDomain(
                    constants.configuration.canvasUiDomain
                );
                const balanceDomain = constants.configuration.balanceDomain;
                const spravIFrameUrl = UrlHelpers.getSpravIframeUrl(
                    constants.configuration.spravIFrameUrl
                );
                const minusPhraseLibraryConstants =
                    constants.validation.libMinusKeywordsConstants;
                const {
                    adGroupConstants,
                    campaignConstants
                } = constants.validation;
                const alertData = {
                    ...constants.alertData,
                    textRu: constants.alertData.textRu || '',
                    textEn: constants.alertData.textEn || ''
                };
                const adGroupsConstants = constants.validation.adGroupConstants;
                const operatorInfo = result.data.operator.info;
                const operatorLogin = operatorInfo ? operatorInfo.login : '';

                const officeInfo = result.data.operator.officeInfo;

                const operator = {
                    tuneSecretKey:
                        result.data.operator.tuneSecretKey || undefined,
                    officeInfo: {
                        cityContact: officeInfo.cityContact
                            ? mapOfficeContacts(officeInfo.cityContact)
                            : undefined,
                        regionContacts: mapRegionContacts([
                            officeInfo.regionContact,
                            officeInfo.regionContact2
                        ])
                    },
                    login: operatorLogin,
                    isSelfAgency: result.data.operator.isSelfAgency,
                    isAgency: result.data.operator.isAgency,
                    ...result.data.operator.features
                };
                const defaultValues = constants.defaultValues;

                return {
                    constants: {
                        ...getClientConstData(),
                        adGroupConstants,
                        campaignConstants,
                        strategyConstants,
                        currencyConstants,
                        alertData,
                        agencyOfflineReportMaximumDate,
                        minusPhraseLibraryConstants,
                        adGroupsConstants,
                        canvasDomain,
                        balanceDomain,
                        spravIFrameUrl,
                        defaultValues,
                        // Стаб. Задача на бэк - DIRECT-98742
                        relevantPhrasesConstants: {
                            minLimit: 1,
                            maxLimit: 100
                        },
                        // стаб констант блока уведомлений
                        // задача на бэк DIRECT-99556
                        minPercentOfCurrentBalanceForSendNotification: 1,
                        maxPercentOfCurrentBalanceForSendNotification: 50,
                        // https://st.yandex-team.ru/DIRECT-100605
                        experimentsConstants: {
                            minRate: -100,
                            maxRate: 1200,
                            activeExperimentsLimit: 5
                        }
                    },
                    availableCampaignTypes,
                    accessRights,
                    wallet: mapApiWalletsToWalletData(
                        result.data.client.wallets
                    ),
                    operator,
                    client: mapApiClientToClientData(result.data.client, login),
                    features: {
                        ...result.data.client.features
                    },
                    operatorFeatures: {
                        ...result.data.operator.features
                    }
                };
            });
    }
}
